import AddIcon from "@mui/icons-material/Add";
import HandymanIcon from "@mui/icons-material/Handyman";
import KeyboardDoubleArrowUpIcon from "@mui/icons-material/KeyboardDoubleArrowUp";
import PersonIcon from "@mui/icons-material/Person";
import SupervisorAccountIcon from "@mui/icons-material/SupervisorAccount";
import { Box, Card, Grid, Tab, Tabs, Typography, Tooltip } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import Lottie from "react-lottie";
import { useSelector } from "react-redux";

import { assets } from "../../../assets";
import { EBenefitTypes } from "../../../permissions/benefits/enums/benefits.enums";
import { selectEntitlement } from "../../../permissions/benefits/redux/benefits.selectors";
import { useAppSelector } from "../../../redux/hooks/redux";
import { getWorkForces } from "../../../utils";
import { BaseModalComponent } from "../../common/BaseModalComponent";
import { SnackbarAlert } from "../../common/SnackbarAlert";
import { SeatLimitProgress } from "../../common/SeatLimitProgress";
import { useSnackbar } from "../../common/snackbar/useSnackbar";
import { CustomFabModal } from "../../shared/components/FABs/CustomFabModal";
import { colors } from "../../ui/colors";
import { useDialog } from "../../ui/dialogs/useDialog";
import { WorkforceCreateView } from "../../workforce/views/WorkforceCreateView";
import { useWorkforceDeleteMutation } from "../hooks/useDeleteWorkforceMutation";
import { QuickPreview } from "./QuickPreview";
import { WorkforceDetail } from "./WorkforceDetail";

const ROLES = [
  { key: "supervisor", label: "Supervisor", icon: <SupervisorAccountIcon /> },
  { key: "contractor", label: "Contractor", icon: <HandymanIcon /> },
  { key: "employee", label: "Employee", icon: <PersonIcon /> },
];

const TABS_SX = {
  minHeight: 48,
  "& .MuiTabs-indicator": {
    backgroundColor: colors.orange.base,
    height: 2.5,
    borderRadius: "2px 2px 0 0",
  },
};

const TAB_SX = (isSelected) => ({
  textTransform: "none",
  fontWeight: isSelected ? 600 : 500,
  fontSize: "0.85rem",
  minHeight: 48,
  transition: "all 0.2s ease",
  color: isSelected ? colors.orange.base : colors.gray[400],
  opacity: isSelected ? 1 : 0.8,
  "&:hover": {
    opacity: 1,
    color: colors.gray[600],
  },
  "&.Mui-selected": {
    color: colors.orange.base,
  },
  "& .MuiSvgIcon-root": {
    fontSize: 18,
    marginRight: 1,
  },
});

const WorkforceCardItem = ({ workforce, onRoleChanged }) => {
  const { mutate: deleteWorkforce } = useWorkforceDeleteMutation();
  const { showDialog, DialogComponent } = useDialog();
  const { snackbar, showSnackbar, closeSnackbar } = useSnackbar();

  const handleDeleteClick = () => {
    showDialog({
      type: "warning",
      title: "Delete workforce member",
      message: `Are you sure you want to delete ${workforce?.firstName} ${workforce?.lastName}? This action cannot be undone.`,
      confirmLabel: "Yes, delete",
      cancelLabel: "Cancel",
      onConfirm: () => {
        deleteWorkforce(workforce?.uid, {
          onSuccess: () => showSnackbar(`${workforce?.firstName} ${workforce?.lastName} has been deleted.`, "success"),
          onError: (error) =>
            showSnackbar(error?.response?.data?.message || error?.message || "Something went wrong while deleting. Please try again.", "error"),
        });
      },
      onCancel: () => { },
    });
  };

  console.log("Datos del trabajador individual:", workforce);
  return (
    <>
      {DialogComponent}
      <SnackbarAlert
        openState={snackbar.open}
        setOpenState={closeSnackbar}
        message={snackbar.message}
        severity={snackbar.severity}
        position="bottom-center"
      />
      <Card
        elevation={0}
        sx={{
          cursor: "pointer",
          height: "auto",
          bgcolor: "var(--fx-surface-base, #FFFFFF)",
          borderRadius: "12px",
          border: `1px solid ${colors.gray[200]}`,
          transition: "all 0.2s ease",
          p: 0,
          "&:hover": {
            borderColor: colors.orange.base,
            boxShadow: `0 3px 10px ${colors.orange[100]}`,
            transform: "translateY(-2px)",
          },
        }}
      >
        <BaseModalComponent
          modalLabel={`${workforce?.workforceRole || workforce?.role || ""} - ${workforce.firstName || ""} ${workforce.lastName || ""}`}
          modalKey={`WorkForceCard-Business-${workforce.uid}`}
          sx={{
            width: { xs: "90%", sm: "80%", md: "100%", lg: "1600px" },
            height: { xs: "95%", sm: "90%", md: "100%", lg: "100%" },
          }}
          elementTrigger={<QuickPreview workforce={workforce} onDeleteClick={handleDeleteClick} />}
        >
          <WorkforceDetail workforce={workforce} onRoleChanged={onRoleChanged} />
        </BaseModalComponent>
      </Card>
    </>
  );
};

export const WorkforceList = () => {
  const numberOfSeats = useAppSelector(selectEntitlement(EBenefitTypes.SEATS));
  const seatLimit = numberOfSeats?.limit;
  const usedSeats = numberOfSeats?.used;
  const remainingSeats = seatLimit - usedSeats;
  const isAtLimit = usedSeats >= seatLimit;

  const [selectedTab, setSelectedTab] = useState(0);

  const { companies } = useSelector((state) => state.userState);

  const handleRoleSwitch = (newRole) => {
    const newTabIndex = ROLES.findIndex((r) => r.key === newRole);
    if (newTabIndex !== -1) {
      setSelectedTab(newTabIndex);
    }
  };
  const [company] = companies;

  const { data: workForces = [] } = useQuery({
    queryKey: ["workforces"],
    queryFn: () => getWorkForces(sessionStorage.getItem("token"), company?.id),
    enabled: !!company?.id,
  });

  console.log("Lista completa de trabajadores:", workForces);
  const filteredWorkforces = useMemo(() => workForces.filter((e) => e.workforceRole === ROLES[selectedTab].key), [workForces, selectedTab]);
  console.log("Trabajadores filtrados:", filteredWorkforces);
  return (
    <Box sx={{ position: "relative", height: "100%", display: "flex", flexDirection: "column", bgcolor: "var(--fx-surface-base, #FFFFFF)" }}>
      <Box sx={{ borderBottom: `1px solid ${colors.gray[200]}`, zIndex: 2 }}>
        <Tabs value={selectedTab} onChange={(_e, v) => setSelectedTab(v)} variant="fullWidth" sx={TABS_SX}>
          {ROLES.map(({ key, icon, label }, i) => (
            <Tab key={key} icon={icon} iconPosition="start" label={label} sx={TAB_SX(selectedTab === i)} />
          ))}
        </Tabs>
      </Box>

      <SeatLimitProgress sx={{ borderBottom: `1px solid ${colors.gray[100]}` }} hasSubscription={numberOfSeats !== undefined} />

      <Box sx={{ flex: 1, overflowY: "auto", p: 2 }}>
        {filteredWorkforces.length > 0 ? (
          <Grid container spacing={1}>
            {filteredWorkforces.map((workforce) => (
              <Grid item xs={12} key={workforce.uid}>
                <WorkforceCardItem workforce={workforce} onRoleChanged={handleRoleSwitch} />
              </Grid>
            ))}
          </Grid>
        ) : (
          <Box
            sx={{
              flex: 1,
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              alignItems: "center",
              textAlign: "center",
              color: colors.gray[600],
              py: 6,
            }}
          >
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
              Looks like there are no workforce members in the "{ROLES[selectedTab].label}" role yet.
            </Typography>
            <Lottie options={{ animationData: assets.animation.notFound, loop: false }} height={150} />
          </Box>
        )}
      </Box>

      <CustomFabModal
        icon={<AddIcon />}
        tooltip={isAtLimit ? "Seat limit reached — upgrade to add more" : "Add new workforce member"}
        modalKey="Dashboard-Workforce-Create-List"
        modalLabel="Add Workforce Member"
        disabled={isAtLimit}
      >
        <WorkforceCreateView />
      </CustomFabModal>
    </Box>
  );
};
